import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Paper, Stack } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { AccessPermission } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import TooltipForDisabledAction from "@entrilia/shared/components/TooltipForDisabledAction";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext";
import { ClientPermissionsContextProvider } from "@entrilia/shared/contexts/ClientPermissionsContextProvider";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import useRequest from "@entrilia/shared/hooks/useRequest";
import { logError } from "@entrilia/shared/logging";
import { combineComparers, numberComparerBy, numberComparerDescBy } from "@entrilia/shared/utilities/arrayHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import biApi from "../../../../api/biApi";
import type { SmartViewArtifact } from "../../../../api/types/smartViewArtifactsTypes";
import type { ReportAuthorizationInfo, ReportGroup, SharedWithEntity } from "../../../../api/types/svTypes";
import useClientContext from "../../../../context/useClientContext";
import AddGroupDialog from "../report-lists/groups/actions/AddGroupDialog";
import { GroupsContextProvider } from "../report-lists/groups/contexts/GroupsContextProvider";
import useGroups from "../report-lists/groups/hooks/useGroups";
import { applyGroupSharedWith } from "../report-lists/hooks/applyGroupSharedWith";
import MoveReportsToGroup from "../report-lists/reports/components/MoveReportsToGroup";
import ReportsPageContainer from "../report-lists/reports/components/ReportsPageContainer";
import type { SideMenuFilter } from "../report-lists/reports/components/ReportsSideMenu";
import ReportsSideMenu from "../report-lists/reports/components/ReportsSideMenu";
import { filterReportList } from "../report-lists/reports/grid/reportListFilter";
import {
  getActiveSideMenuFilter,
  getSideMenuFilterLabel,
  getSideMenuItemFields,
  isInSideMenuFilter,
} from "../report-lists/reports/hooks/sideMenuItems";
import useFavoriteReports from "../report-lists/reports/hooks/useFavoriteReports";
import { ReportTypesEnum } from "../report-lists/reports/types/reportTypes";
import { getArtifactPageBackground } from "./artifactPageBackground";
import ArtifactShareDialog from "./ArtifactShareDialog";
import ArtifactsGrid from "./ArtifactsGrid";
import type { ArtifactRow } from "./list/artifactRow";
import ArtifactsListFilterBar from "./list/ArtifactsListFilterBar";
import type { ArtifactsTileSort, ArtifactsView } from "./list/artifactsListView";
import {
  readArtifactsTileSort,
  readArtifactsView,
  saveArtifactsTileSort,
  saveArtifactsView,
} from "./list/artifactsListView";
import ArtifactsTiles from "./list/ArtifactsTiles";
import { getRecentArtifactTiles } from "./list/recentArtifacts";
import { getArtifactEditUrl } from "./list/artifactUrls";
import DeleteArtifactsDialog from "./list/DeleteArtifactsDialog";
import useCanManageArtifacts from "./useCanManageArtifacts";

const reportManagementPermissions: AccessPermission[] = ["ManageReports", "ShareReports", "ManageAllReports"];

const toArtifactListPermissions = (permissions: AccessPermission[], canManage: boolean): AccessPermission[] =>
  canManage
    ? [...new Set([...permissions, ...reportManagementPermissions])]
    : permissions.filter((permission) => !reportManagementPermissions.includes(permission));

const getModifiedTime = (artifact: SmartViewArtifact): number =>
  new Date(artifact.modifiedAt ?? artifact.createdAt).getTime();

const getRowGroupOrder = (row: ArtifactRow): number => row.groupOrder;

const toRows = (
  artifacts: SmartViewArtifact[],
  groups: ReportGroup[],
  filter: SideMenuFilter,
  favoriteIds: Set<string>
): ArtifactRow[] => {
  const groupsById = new Map(groups.map((group) => [group.id, group]));
  return artifacts
    .map((artifact) => ({
      ...artifact,
      ...getSideMenuItemFields(groupsById.get(artifact.groupId), favoriteIds.has(artifact.id)),
    }))
    .filter((row) => isInSideMenuFilter(filter, row))
    .sort(combineComparers(numberComparerBy(getRowGroupOrder), numberComparerDescBy(getModifiedTime)));
};

const getArtifactId = (row: ArtifactRow) => row.id;

const getNoRowsText = (artifactCount: number, rowCount: number, filter: SideMenuFilter): string => {
  if (artifactCount === 0) {
    return "No dashboards yet";
  }
  if (rowCount > 0) {
    return "No dashboards found";
  }
  return filter === "favorites" ? "No favorite dashboards yet" : "No dashboards in this group";
};

const withArtifactSharing =
  (artifactId: string, authorization: ReportAuthorizationInfo, inheritFromParent: boolean) =>
  (artifacts: SmartViewArtifact[] | undefined) =>
    artifacts?.map((artifact) =>
      artifact.id === artifactId ? { ...artifact, authorization, inheritFromParent } : artifact
    );

const countByGroup = (artifacts: SmartViewArtifact[]): Record<string, number> =>
  artifacts.reduce<Record<string, number>>((counts, artifact) => {
    counts[artifact.groupId] = (counts[artifact.groupId] ?? 0) + 1;
    return counts;
  }, {});

const countByType = (rowsByType: Record<ReportTypesEnum, ArtifactRow[]>): Record<ReportTypesEnum, number> => ({
  [ReportTypesEnum.All]: rowsByType[ReportTypesEnum.All].length,
  [ReportTypesEnum.MyReports]: rowsByType[ReportTypesEnum.MyReports].length,
  [ReportTypesEnum.SharedWithMe]: rowsByType[ReportTypesEnum.SharedWithMe].length,
});

interface MoveTarget {
  artifactIds: string[];
  onMoved?: () => void;
}

interface DeleteTarget {
  artifacts: SmartViewArtifact[];
  onDeleted?: () => void;
}

const ArtifactsListPage = () => {
  usePageTitle("AI Dashboards");
  const navigate = useNavigate();
  const notification = useNotificationContext();
  const { clientCode } = useClientContext();
  const { permissions, permissionSets } = useClientPermissionsContext();
  const canManage = useCanManageArtifacts();

  const getArtifacts = useCallback((signal?: AbortSignal) => biApi.smartViewArtifacts.list(signal), []);
  const [artifacts, artifactsError, { isFetching, fetch: refetchArtifacts, setData: setArtifacts }] =
    useFetch(getArtifacts);
  const applyGroupSharing = useCallback(
    (groupId: string, sharedWith: SharedWithEntity[]) =>
      setArtifacts((artifacts) => artifacts && applyGroupSharedWith(artifacts, groupId, sharedWith)),
    [setArtifacts]
  );
  const {
    groups,
    actions: groupActions,
    ui: groupsUi,
  } = useGroups({ ...biApi.smartViewArtifactGroups, applyGroupSharing });
  const { load: loadGroups } = groupActions;
  const { favoriteIds, toggleFavorite } = useFavoriteReports({
    clientCode,
    getFavoriteReports: biApi.uiReports.getFavoriteReports,
    toggleFavoriteReport: biApi.uiReports.toggleFavoriteReport,
  });
  const [recentArtifacts, recentArtifactsError] = useFetch(biApi.smartViewArtifacts.listRecent);

  const [selectedFilter, setSelectedFilter] = useState<SideMenuFilter>("all");
  const [selectedType, setSelectedType] = useState<ReportTypesEnum>(ReportTypesEnum.All);
  const [searchText, setSearchText] = useState("");
  const [view, setView] = useState<ArtifactsView>(readArtifactsView);
  const [tileSort, setTileSort] = useState<ArtifactsTileSort>(readArtifactsTileSort);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>();
  const [moveTarget, setMoveTarget] = useState<MoveTarget>();
  const [shareTarget, setShareTarget] = useState<SmartViewArtifact>();
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);

  useEffect(() => {
    loadGroups(clientCode);
  }, [clientCode, loadGroups]);

  const listPermissions = useMemo(() => toArtifactListPermissions(permissions, canManage), [permissions, canManage]);
  const listPermissionSets = useMemo(
    () =>
      permissionSets.map((permissionSet) =>
        permissionSet.clientCode === clientCode
          ? { ...permissionSet, permissions: toArtifactListPermissions(permissionSet.permissions, canManage) }
          : permissionSet
      ),
    [permissionSets, clientCode, canManage]
  );

  const sortedGroups = useMemo(
    () => [...(groups[clientCode] ?? [])].sort((left, right) => left.order - right.order),
    [groups, clientCode]
  );
  const activeFilter = getActiveSideMenuFilter(selectedFilter, sortedGroups);
  const selectedGroup = sortedGroups.find((group) => group.id === activeFilter);
  const targetGroupId = (selectedGroup ?? sortedGroups[0])?.id;

  const rows = useMemo(
    () => toRows(artifacts ?? [], sortedGroups, activeFilter, favoriteIds),
    [artifacts, sortedGroups, activeFilter, favoriteIds]
  );
  const rowsByType = useMemo(() => filterReportList(rows, searchText, getArtifactId), [rows, searchText]);
  const countsByGroup = useMemo(() => countByGroup(artifacts ?? []), [artifacts]);

  const createArtifactCall = useCallback(
    () => biApi.smartViewArtifacts.create(defined(targetGroupId)),
    [targetGroupId]
  );
  const { request: createArtifact, isLoading: isCreating } = useRequest(createArtifactCall);

  const handleCreate = useCallback(async () => {
    const [artifact, error] = await createArtifact();
    if (error) {
      if (!error.isCanceledRequest) {
        logError(error, "[ArtifactsListPage] createArtifact");
      }
      notification.sendNotificationError("Failed to create the dashboard. Please try again.");
      return;
    }
    if (artifact) {
      navigate(getArtifactEditUrl(artifact), { state: { focusChat: true } });
    }
  }, [createArtifact, navigate, notification]);

  const handleChangeGroup = useCallback(
    (artifactIds: string[], onMoved?: () => void) => setMoveTarget({ artifactIds, onMoved }),
    []
  );

  const handleDelete = useCallback(
    (artifactsToDelete: ArtifactRow[], onDeleted?: () => void) =>
      setDeleteTarget({ artifacts: artifactsToDelete, onDeleted }),
    []
  );

  const handleCloseDeleteDialog = useCallback(() => setDeleteTarget(undefined), []);
  const handleCloseMoveDialog = useCallback(() => setMoveTarget(undefined), []);
  const handleCloseShareDialog = useCallback(() => setShareTarget(undefined), []);
  const handleCloseAddGroupDialog = useCallback(() => setShowAddGroupDialog(false), []);

  const handleViewChange = (nextView: ArtifactsView) => {
    setView(nextView);
    saveArtifactsView(nextView);
  };

  const handleTileSortChange = (nextSort: ArtifactsTileSort) => {
    setTileSort(nextSort);
    saveArtifactsTileSort(nextSort);
  };

  if (artifactsError) {
    logError(artifactsError, "[ArtifactsListPage] getArtifacts");
    return <DataLoadingFailed title="Failed to load dashboards" text={artifactsError.message} />;
  }

  if (groupsUi.error !== undefined) {
    return <DataLoadingFailed title="Failed to load dashboard groups" text={groupsUi.error} />;
  }

  if (artifacts === undefined || !groupsUi.loaded || (recentArtifacts === undefined && !recentArtifactsError)) {
    return (
      <Stack sx={{ height: "100%" }}>
        <InlineLoader text="Loading dashboards..." />
      </Stack>
    );
  }

  if (recentArtifactsError) {
    logError(recentArtifactsError, "[ArtifactsListPage] listRecent");
  }

  const listTitle = getSideMenuFilterLabel(activeFilter, sortedGroups, "All Dashboards");
  const noRowsText = getNoRowsText(artifacts.length, rows.length, activeFilter);
  const shownRows = rowsByType[selectedType];
  const isGroupSelected = selectedGroup !== undefined;
  const isRecentShown =
    view === "tiles" && activeFilter === "all" && selectedType === ReportTypesEnum.All && searchText.trim() === "";
  const recentTiles = isRecentShown ? getRecentArtifactTiles(recentArtifacts ?? [], shownRows) : [];

  const filterBar = (
    <ArtifactsListFilterBar
      canManage={canManage}
      counts={countByType(rowsByType)}
      selectedType={selectedType}
      searchText={searchText}
      view={view}
      sort={view === "tiles" ? tileSort : undefined}
      onTypeChange={setSelectedType}
      onSearch={setSearchText}
      onViewChange={handleViewChange}
      onSortChange={handleTileSortChange}
    />
  );

  return (
    <ClientPermissionsContextProvider permissions={listPermissions} permissionSets={listPermissionSets}>
      <GroupsContextProvider
        groups={groups}
        ui={groupsUi}
        actions={{ ...groupActions, reloadReports: refetchArtifacts }}
      >
        <Stack direction="row" sx={{ height: "100%", width: "100%" }}>
          <ReportsSideMenu
            title="AI Dashboards"
            selectedFilter={activeFilter}
            onFilterChange={setSelectedFilter}
            groups={sortedGroups}
            reportCountsByGroup={countsByGroup}
            groupType="dashboard"
            onAddGroup={() => setShowAddGroupDialog(true)}
            onReorderGroups={(reorderedGroups) => groupActions.reorder(clientCode, reorderedGroups)}
            allItemsLabel="All Dashboards"
            groupsLabel="Dashboards by Group"
          />
          <Box sx={{ flex: 1, minWidth: 0, bgcolor: getArtifactPageBackground }}>
            <ReportsPageContainer
              title={listTitle}
              Actions={
                canManage && (
                  <TooltipForDisabledAction
                    title={targetGroupId ? undefined : "Create a group first"}
                    isActionDisabled={!targetGroupId}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isCreating || !targetGroupId}
                      loading={isCreating}
                      onClick={handleCreate}
                    >
                      <AddRoundedIcon />
                      New Dashboard
                    </Button>
                  </TooltipForDisabledAction>
                )
              }
            >
              <Stack spacing={2} sx={{ flex: 1, minHeight: 0, px: 3, pb: 1 }}>
                {view === "tiles" ? (
                  <ArtifactsTiles
                    key={activeFilter}
                    rows={shownRows}
                    recentTiles={recentTiles}
                    canManage={canManage}
                    isGroupSelected={isGroupSelected}
                    toolbar={filterBar}
                    noRowsText={noRowsText}
                    sort={tileSort}
                    onToggleFavorite={toggleFavorite}
                    onChangeGroup={handleChangeGroup}
                    onShare={setShareTarget}
                    onDelete={handleDelete}
                  />
                ) : (
                  <>
                    {filterBar}
                    <Paper variant="outlined" sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
                      <ArtifactsGrid
                        rows={shownRows}
                        loading={isFetching}
                        noRowsText={noRowsText}
                        canManage={canManage}
                        isGroupSelected={isGroupSelected}
                        onToggleFavorite={toggleFavorite}
                        onChangeGroup={handleChangeGroup}
                        onShare={setShareTarget}
                        onDelete={handleDelete}
                      />
                    </Paper>
                  </>
                )}
              </Stack>
            </ReportsPageContainer>
          </Box>
        </Stack>
        {deleteTarget && (
          <DeleteArtifactsDialog
            artifacts={deleteTarget.artifacts}
            onClose={handleCloseDeleteDialog}
            onDeleted={() => {
              deleteTarget.onDeleted?.();
              refetchArtifacts();
            }}
          />
        )}
        {moveTarget && (
          <MoveReportsToGroup
            reports={moveTarget.artifactIds.map((reportId) => ({ clientCode, reportId }))}
            groupType="dashboard"
            doMove={biApi.smartViewArtifactGroups.changeReportsGroup}
            onMoved={() => {
              notification.sendNotification(
                moveTarget.artifactIds.length > 1
                  ? "The group was changed for the selected dashboards."
                  : "The group was changed for the selected dashboard."
              );
              moveTarget.onMoved?.();
              refetchArtifacts();
            }}
            onClose={handleCloseMoveDialog}
          />
        )}
        {shareTarget && (
          <ArtifactShareDialog
            artifact={shareTarget}
            name={shareTarget.name}
            onClose={handleCloseShareDialog}
            onSharingUpdated={(authorization, inheritFromParent) =>
              setArtifacts(withArtifactSharing(shareTarget.id, authorization, inheritFromParent))
            }
          />
        )}
        {showAddGroupDialog && <AddGroupDialog clientCode={clientCode} onClose={handleCloseAddGroupDialog} />}
      </GroupsContextProvider>
    </ClientPermissionsContextProvider>
  );
};

export default ArtifactsListPage;
